import { SearchOutlined } from '@ant-design/icons';
import { Button, Checkbox, Input } from 'antd';
import { FilterDropdownProps } from 'antd/es/table/interface';
import { FC, useState } from 'react';

export function makeFilterDropdown(
  allFilters: { text: string; value: string }[],
  isDarkMode?: boolean
): (props: FilterDropdownProps) => React.ReactNode {
  const dark = isDarkMode ?? false;
  const bg = dark ? '#130D1E' : '#FFFFFF';
  const textColor = dark ? '#F0EBF8' : '#2E1548';
  const mutedColor = dark ? '#9B8DB8' : '#999';
  const borderColor = dark ? '#2D1F4A' : '#E8E0F0';

  const FilterDropdown: FC<FilterDropdownProps> = ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
    filters: _filters,
  }) => {
    const [search, setSearch] = useState('');

    const filtered = search
      ? allFilters.filter((o) =>
          o.text.toLowerCase().includes(search.toLowerCase())
        )
      : allFilters;

    const selected = selectedKeys as string[];
    const allVisibleSelected =
      filtered.length > 0 && filtered.every((o) => selected.includes(o.value));

    const handleSelectAll = (checked: boolean) => {
      if (checked) {
        const newKeys = new Set(selected);
        filtered.forEach((o) => newKeys.add(o.value));
        setSelectedKeys([...newKeys]);
      } else {
        const removeSet = new Set(filtered.map((o) => o.value));
        setSelectedKeys(selected.filter((k) => !removeSet.has(k)));
      }
    };

    return (
      <div
        style={{
          padding: 8,
          width: 250,
          background: bg,
          borderRadius: 6,
          border: `1px solid ${borderColor}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 8,
          }}
        >
          <Checkbox
            checked={allVisibleSelected}
            indeterminate={
              !allVisibleSelected &&
              filtered.some((o) => selected.includes(o.value))
            }
            onChange={(e) => handleSelectAll(e.target.checked)}
            style={{ flexShrink: 0 }}
          />
          <Input
            placeholder="Search..."
            prefix={<SearchOutlined style={{ color: mutedColor }} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            size="small"
            style={{ flex: 1 }}
          />
          <Button
            size="small"
            onClick={() => {
              setSearch('');
              clearFilters?.();
            }}
            style={{ flexShrink: 0 }}
          >
            Reset
          </Button>
        </div>
        <div style={{ maxHeight: 260, overflowY: 'auto' }}>
          {filtered.map((o) => (
            <div key={o.value} style={{ padding: '2px 0' }}>
              <Checkbox
                checked={selected.includes(o.value)}
                onChange={(e) => {
                  const next = e.target.checked
                    ? [...selected, o.value]
                    : selected.filter((k) => k !== o.value);
                  setSelectedKeys(next);
                }}
              >
                <span style={{ fontSize: 13, color: textColor }}>{o.text}</span>
              </Checkbox>
            </div>
          ))}
          {filtered.length === 0 && (
            <div
              style={{
                color: mutedColor,
                textAlign: 'center',
                padding: '8px 0',
              }}
            >
              No matches
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right', marginTop: 8 }}>
          <Button type="primary" size="small" onClick={() => confirm()}>
            OK
          </Button>
        </div>
      </div>
    );
  };

  return (props: FilterDropdownProps) => <FilterDropdown {...props} />;
}
